import _stripTags from "../functions/quill/stripTags.js";
import _autoFormatCheck from "../functions/eventGroups/autoFormatCheck.js";
import _autoFormat from "../functions/eventGroups/autoFormat.js";
import _sceneristAdvanced from "./sceneristAdvanced.js";
import _sccLookup from "../dict/608.js";
import _sccFunc from "../functions/profiles/scenerist.js";
import _eol from "eol";
import _getFormatOptions from "../functions/helpers/getFormatOptions.js";
import _removeInvalidEvents from "../functions/eventGroups/removeInvalidEvents.js";
import _tcLib from "../lib/timecode.js";
/* 
    Title Safe Info:
    http://www.indefilms.net/html/title_safe_area.html
    SCC INFO:
    -We work on a grid that is 0-31 across and 0-14 down (32x15)
    -There is a fixed offset of 10% (x) and 10% (y). This is due to the caption area being 90% of the frames width and 90% of the frames height.
    -The fixed offset is only applied to decode. For encode we try and fit it to the best location.
    -The fixed offset is applied during post-process decode.
*/
const tcLib = _tcLib;
const removeInvalidEvents = _removeInvalidEvents;
const getFormatOptions = _getFormatOptions;
const eol = _eol;
const sccFunc = _sccFunc;
const sccLookup = _sccLookup;
const sceneristAdvanced = _sceneristAdvanced;
const autoFormat = _autoFormat;
const autoFormatCheck = _autoFormatCheck;
const stripTags = _stripTags;

//A plain mid-row color code turns italics and underline off. Close every tag still open on this line
//(innermost first, the new color tag is opened right after) so closing tags are not discarded later as unbalanced.
function closeOpenStyles(text) {
  const stack = [];
  for (const match of text.matchAll(/<(\/?)([A-Za-z]+)>/g)) {
    if (match[1]) {
      const at = stack.lastIndexOf(match[2]);
      if (at > -1) {
        stack.splice(at, 1);
      }
    } else {
      stack.push(match[2]);
    }
  }
  if (stack.length === 0) {
    return text;
  }

  //Keep any trailing space outside of the formatting
  const trailing = text.match(/ *$/)[0];
  const closing = stack.reverse().map(tag => `</${tag}>`).join("");
  return text.slice(0, text.length - trailing.length) + closing + trailing;
}

//True when the next code pair is (or starts with) a space character
function nextCodeStartsWithSpace(nextCode) {
  if (!nextCode) {
    return false;
  }
  return sccLookup.all[nextCode] === " " || sccLookup.all[nextCode.substring(0, 2)] === " ";
}
function detectCaptionChannel(sccContent) {
  // Define all the display codes for each channel
  const channelCodes = {
    ch01: ['9420',
    // Resume Caption Loading
    '9425',
    // Roll-Up 2 Rows
    '9426',
    // Roll-Up 3 Rows
    '94a7',
    // Roll-Up 4 Rows
    '9429' // Paint-on
    ],
    ch02: ['1c20',
    // Resume Caption Loading
    '1c25',
    // Roll-Up 2 Rows
    '1c26',
    // Roll-Up 3 Rows
    '1ca7',
    // Roll-Up 4 Rows
    '1c29' // Paint-on
    ],
    ch03: ['1520',
    // Resume Caption Loading
    '1525',
    // Roll-Up 2 Rows
    '1526',
    // Roll-Up 3 Rows
    '15a7',
    // Roll-Up 4 Rows
    '1529' // Paint-on
    ],
    ch04: ['9d20',
    // Resume Caption Loading
    '9d25',
    // Roll-Up 2 Rows
    '9d26',
    // Roll-Up 3 Rows
    '9da7',
    // Roll-Up 4 Rows
    '9d29' // Paint-on
    ]
  };

  // Convert content to lowercase for case-insensitive matching
  const content = sccContent.toLowerCase();

  // Count occurrences for each channel
  const channelCounts = {};
  for (const [channel, codes] of Object.entries(channelCodes)) {
    channelCounts[channel] = 0;

    // Count each code for this channel
    for (const code of codes) {
      const regex = new RegExp(code, 'g');
      const matches = content.match(regex);
      if (matches) {
        channelCounts[channel] += matches.length;
      }
    }
  }

  // Find the channel with the highest count
  let maxCount = 0;
  let detectedChannel = 'ch01'; // Default fallback

  for (const [channel, count] of Object.entries(channelCounts)) {
    if (count > maxCount) {
      maxCount = count;
      detectedChannel = channel;
    }
  }
  return detectedChannel;
}
export default {
  decode: function (input, options) {
    let events = [],
      displays = [];
    let buffer = new sccFunc.Display(),
      screen = false,
      decodingOptions = getFormatOptions(options.formatOptions),
      clock,
      displayStyle = false,
      sccCode,
      fileLineTc;
    buffer.insertLine();
    let channel = decodingOptions["Channel"] || "auto";
    if (channel.toLowerCase() === 'auto') {
      channel = detectCaptionChannel(input);
    }

    /* Split File lines and filter anything that doesn't have a timecode */
    let fileLines = input.split("\n").filter(fileLine => {
      return /^\d\d:\d\d:\d\d;\d\d$|^\d\d:\d\d:\d\d:\d\d$|^\d\d;\d\d;\d\d;\d\d$/g.test(fileLine.split("\t")[0]);
    });

    //Frame Rate scan:
    let highestFrameValue = 0;
    fileLines.forEach((fileLine, index, lines) => {
      fileLineTc = fileLine.split("\t")[0];
      //If fileLineTc is formatted as hh;mm;ss;ff then replace the first two semicolons with colons so that we can properly parse the timecode and frame value. e.g. 01;00;00;24 -> 01:00:00;24
      if (/^\d\d;\d\d;\d\d;\d\d$/.test(fileLineTc)) {
        fileLineTc = fileLineTc.replace(";", ":").replace(";", ":");
      }

      //get the last two digits of the smpte timecode (e.g. 01:00:00:24 -> 24 or 01:00:00;24 -> 24)
      let frameValue = fileLineTc.split(":").pop().split(";").pop();
      if (parseInt(frameValue) === Math.round(options.frameRate)) {
        //Update the timecode in the file line to match the options.frameRate-1. We must be specific to only change the frame value and not any other part of the timecode. The frame value is followed by a tab character (e.g 01:14:12:04\t)
        lines[index] = fileLine.replace(/(?<=\d{2}[;:]\d{2}[;:]\d{2}[;:])\d\d\b/, (Math.round(options.frameRate) - 1).toString());
        //console.log(lines[index]);
      } else if (parseInt(frameValue) > highestFrameValue) {
        highestFrameValue = parseInt(frameValue);
      }
    });
    if (highestFrameValue > options.frameRate) {
      console.log(`WARN: File has frame value greater than set framerate: ${highestFrameValue} vs. ${options.frameRate}`);
    }
    fileLines.forEach(fileLine => {
      fileLineTc = fileLine.split("\t")[0];
      if (/^\d\d;\d\d;\d\d;\d\d$/.test(fileLineTc)) {
        fileLineTc = fileLineTc.replace(";", ":").replace(";", ":");
      }
      // console.log(fileLineTc, options.frameRate, options.dropFrame);
      clock = tcLib.createTc(fileLineTc, options.frameRate, options.dropFrame);

      // console.log("Start of Line:", clock.toString());

      /* Get all caption codes */
      let captionCodes = fileLine.split("\t")[1].trim().split(" ");
      let skippedRepeat = false;
      captionCodes.forEach(function (code, index, codes) {
        /* Each code word takes one frame: word N of a line is sent at the line timecode + N frames */
        clock = tcLib.createTc(fileLineTc, options.frameRate, options.dropFrame);
        clock.add(index);
        // console.log(`${code} added at tc: ${clock.toString()}`);
        if (!code) {
          return;
        }
        if (code.length !== 4) {
          if (code.length === 2) {
            code += "80";
          } else {
            throw new Error("SCC decode error at timecode: " + clock.toString() + "\nCommand length is not 4: " + code);
          }
        }

        /* Control codes are sent twice. Act on the first one and ignore the repeat that follows it. */
        let isSpecialChar = sccLookup.specialChars.indexOf(sccLookup[channel][code]) > -1;
        let isRepeat = !!sccLookup[channel][code] && !isSpecialChar && code === codes[index - 1] && !skippedRepeat;
        skippedRepeat = isRepeat;
        if (isRepeat) {
          return;
        }
        if (sccLookup[channel][code]) {
          sccCode = sccLookup[channel][code];
          if (sccCode === "{RESUME LOADING}") {
            //console.log(`Resume loading added at tc: ${clock.toString()}`)
            displayStyle = "popOn";
            if (buffer.lines.length > 0 && buffer.lines[0].text !== "") {
              buffer = new sccFunc.Display();
              buffer.insertLine();
            }

            // console.log(`Resume loading added at tc: ${clock.toString()}`)
          } else if (sccCode === "{PAINT ON}") {
            displayStyle = "paintOn";
            screen = new sccFunc.Display({
              style: "Paint-On",
              start: tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame)
            });
            screen.insertLine();
          } else if (sccCode === "{ROLLUP2}") {
            if (displayStyle !== "rollUp2") {
              displayStyle = "rollUp2";
              /* Add outcode for the top line of event */
              if (displays.length > 0 && displays[displays.length - 1].style === "Roll-Up 2" && !displays[displays.length - 1].end) {
                displays[displays.length - 1].end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
              }
              if (screen) {
                displays.push(screen);
              }
              screen = new sccFunc.Display({
                style: "Roll-Up 2"
              });
              screen.insertLine({
                yPos: 13
              });
            }
          } else if (sccCode === "{ROLLUP3}") {
            if (displayStyle !== "rollUp3") {
              displayStyle = "rollUp3";

              /* Add outcode for the top line of event */
              if (displays.length > 1 && displays[displays.length - 2].style === "Roll-Up 3" && !displays[displays.length - 2].end) {
                displays[displays.length - 2].end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
              }
              if (screen) {
                displays.push(screen);
              }
              screen = new sccFunc.Display({
                style: "Roll-Up 3"
                // start: tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame),
              });
              screen.insertLine({
                yPos: 12
              });
            }
          } else if (sccCode === "{ROLLUP4}") {
            if (displayStyle !== "rollUp4") {
              displayStyle = "rollUp4";

              /* Add outcode for the top line of event */
              if (displays.length > 2 && displays[displays.length - 3].style === "Roll-Up 4" && !displays[displays.length - 3].end) {
                displays[displays.length - 3].end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
              }
              if (screen) {
                displays.push(screen);
              }
              screen = new sccFunc.Display({
                style: "Roll-Up 4"
                // start: tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame),
              });
              screen.insertLine({
                yPos: 11
              });
            }
          } else if (sccCode === "{TEXT MSG}") {
            /* Not Supported */
          } else if (sccCode === "{RESUME TEXT MSG}") {
            /* Not Supported */
          } else if (sccCode === "{CLEAR BUFFER}") {
            //console.log(`Clear buffer added at tc: ${clock.toString()}`)
            buffer = new sccFunc.Display();
            buffer.insertLine();
          } else if (sccCode === "{CLEAR DISPLAY}") {
            //console.log(screen);
            if (screen && !screen.end) {
              screen.end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);

              //console.log(`Clear Screen added at tc: ${clock.toString()}`)

              displays.push(screen);
              if (screen.style !== "Paint-On" || screen.style !== "Pop-On") {
                screen = new sccFunc.Display({
                  style: screen.style,
                  start: tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame)
                });
                clock.add(1);
                screen.end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
                screen.insertLine({
                  yPos: 11
                });
                displays.push(screen);
              }
              screen = new sccFunc.Display({
                style: screen.style,
                start: screen.style === "Paint-On" || screen.style === "Pop-On" ? tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame) : false
              });
              screen.insertLine({
                yPos: 11
              });
            }
            displays.forEach((display, index, displaysArray) => {
              if (["Roll-Up 2", "Roll-Up 3", "Roll-Up 4", "Paint-On"].indexOf(display.style) > -1 && !display.end) {
                displaysArray[index].end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
              }
            });
          } else if (sccCode === "{DISPLAY BUFFER}") {
            // console.log(`Display Screen added at tc: ${clock.toString()}${JSON.stringify(buffer.lines)}`)
            /* Clear what's on the screen if there is something still being shown */
            if (screen && !screen.end) {
              screen.end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
              displays.push(screen);
            }
            buffer.start = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
            screen = buffer;
            buffer = new sccFunc.Display();
            buffer.insertLine();
          } else if (sccCode === "{ITALICS}") {
            //console.log(`Italics added at: ${clock.toString()}`)
            if (displayStyle === "popOn") {
              if (buffer.lines[buffer.lines.length - 1].text.length > 0 && !buffer.lines[buffer.lines.length - 1].text.endsWith(' ')) {
                buffer.lines[buffer.lines.length - 1].text += " ";
              }
              buffer.lines[buffer.lines.length - 1].text += "<em>";
            } else if (screen) {
              if (screen.lines[screen.lines.length - 1].text.length > 0 && !screen.lines[screen.lines.length - 1].text.endsWith(' ')) {
                screen.lines[screen.lines.length - 1].text += " ";
              }
              screen.lines[screen.lines.length - 1].text += "<em>";
            }
          } else if (sccCode === "{UNDERLINE}") {
            if (displayStyle === "popOn") {
              if (buffer.lines[buffer.lines.length - 1].text.length > 0 && !buffer.lines[buffer.lines.length - 1].text.endsWith(' ')) {
                buffer.lines[buffer.lines.length - 1].text += " ";
              }
              buffer.lines[buffer.lines.length - 1].text += "<u>";
            } else {
              if (screen.lines[screen.lines.length - 1].text.length > 0 && !screen.lines[screen.lines.length - 1].text.endsWith(' ')) {
                screen.lines[screen.lines.length - 1].text += " ";
              }
              screen.lines[screen.lines.length - 1].text += "<u>";
            }
          } else if (sccCode === "{ITALICS_UNDERLINE}") {
            if (displayStyle === "popOn") {
              buffer.lines[buffer.lines.length - 1].text += "<em><u>";
            } else {
              screen.lines[screen.lines.length - 1].text += "<em><u>";
            }
          } else if (/{COLOR:[A-Z]+;UNDERLINE}/.test(sccCode)) {
            /*  
                Matches both commands with position and without. e.g. {00_00}{COLOR:CYAN;UNDERLINE} and {COLOR:CYAN;UNDERLINE}
                We need to parse the position information (if it exists), the color information, and the formatting information.
            */

            // Check if position information exists
            let hasPosition = /^{\d\d_\d\d}/.test(sccCode);
            let xPos, yPos, color;
            if (hasPosition) {
              // Extract position: {YY_XX}{COLOR:...}
              xPos = parseInt(sccCode.substring(4, 6));
              yPos = parseInt(sccCode.substring(1, 3));
              // Extract color from the second part
              color = sccCode.split("{COLOR:")[1].split(";")[0];
            } else {
              // No position, just {COLOR:...;UNDERLINE}
              color = sccCode.split(":")[1].split(";")[0];
            }
            if (displayStyle === "popOn") {
              if (hasPosition) {
                buffer.insertLine({
                  text: "<u>",
                  xPos: xPos,
                  yPos: yPos
                });
                buffer.lines[buffer.lines.length - 1].color = sccFunc.colorMapping[color];
                buffer.lines[buffer.lines.length - 1].text += `<${color}>`;
              } else {
                buffer.lines[buffer.lines.length - 1].color = sccFunc.colorMapping[color];
                //The mid-row code takes up a cell, same as {UNDERLINE}
                if (buffer.lines[buffer.lines.length - 1].text.length > 0 && !buffer.lines[buffer.lines.length - 1].text.endsWith(' ')) {
                  buffer.lines[buffer.lines.length - 1].text += " ";
                }
                buffer.lines[buffer.lines.length - 1].text += "<u>";
                buffer.lines[buffer.lines.length - 1].text += `<${color}>`;
              }
            } else {
              if (hasPosition) {
                screen.insertLine({
                  text: "<u>",
                  xPos: xPos,
                  yPos: yPos
                });
                screen.lines[screen.lines.length - 1].color = sccFunc.colorMapping[color];
                screen.lines[screen.lines.length - 1].text += `<${color}>`;
              } else {
                screen.lines[screen.lines.length - 1].color = sccFunc.colorMapping[color];
                screen.lines[screen.lines.length - 1].text += "<u>";
                screen.lines[screen.lines.length - 1].text += `<${color}>`;
              }
            }
          } else if (/{\d\d_\d\d}{ITALICS_UNDERLINE}/.test(sccCode)) {
            let xPos = parseInt(sccCode.substring(4, 6));
            let yPos = parseInt(sccCode.substring(1, 3));
            if (displayStyle === "popOn") {
              buffer.insertLine({
                text: "<em><u>",
                xPos: xPos,
                yPos: yPos
              });
            } else {
              screen.insertLine({
                text: "<em><u>",
                xPos: xPos,
                yPos: yPos
              });
            }
          } else if (/{\d\d_\d\d}{ITALICS}/.test(sccCode)) {
            let xPos = parseInt(sccCode.substring(4, 6));
            let yPos = parseInt(sccCode.substring(1, 3));
            if (displayStyle === "popOn") {
              buffer.insertLine({
                text: "<em>",
                xPos: xPos,
                yPos: yPos
              });
            } else if (screen) {
              screen.insertLine({
                text: "<em>",
                xPos: xPos,
                yPos: yPos
              });
            }
          } else if (/{\d\d_\d\d}{UNDERLINE}/.test(sccCode)) {
            let xPos = parseInt(sccCode.substring(4, 6));
            let yPos = parseInt(sccCode.substring(1, 3));
            if (displayStyle === "popOn") {
              buffer.insertLine({
                text: "<u>",
                xPos: xPos,
                yPos: yPos
              });
            } else {
              screen.insertLine({
                text: "<u>",
                xPos: xPos,
                yPos: yPos
              });
            }
          } else if (/{\d\d_\d\d}/.test(sccCode)) {
            let xPos = parseInt(sccCode.substring(4, 6));
            let yPos = parseInt(sccCode.substring(1, 3));
            if (displayStyle === "popOn") {
              buffer.insertLine({
                xPos: xPos,
                yPos: yPos
              });
            } else if (screen) {
              screen.insertLine({
                xPos: xPos,
                yPos: yPos
              });
            }
          } else if (sccCode.includes("TAB")) {
            let xOffset = parseInt(sccCode.split("TAB")[1]);
            if (displayStyle === "popOn") {
              buffer.lines[buffer.lines.length - 1].xPos += xOffset;
              // console.log(`Applied TAB with offset ${xOffset} to buffer at tc: ${clock.toString()}`);
              // console.log("New buffer line state:", buffer.lines[buffer.lines.length - 1]);
            } else if (screen) {
              screen.lines[screen.lines.length - 1].xPos += xOffset;
            }
          } else if (sccCode.includes("{COLOR:")) {
            let color = sccCode.split(":")[1].split(";")[0].split("}")[0];
            let underline = sccCode.includes("UNDERLINE");
            if (displayStyle === "popOn") {
              buffer.lines[buffer.lines.length - 1].color = sccFunc.colorMapping[color];
              //A white mid-row code turns italics off. Close <em> before the color tag so it is not discarded as unbalanced.
              if (sccCode === "{COLOR:WHITE}") {
                buffer.lines[buffer.lines.length - 1].text = closeOpenStyles(buffer.lines[buffer.lines.length - 1].text);
              }
              underline ? buffer.lines[buffer.lines.length - 1].text += "<u>" : null;
              buffer.lines[buffer.lines.length - 1].text += `<${color}>`;
              if (sccCode === "{COLOR:WHITE}" && codes[index + 1] && !nextCodeStartsWithSpace(codes[index + 1])) {
                buffer.lines[buffer.lines.length - 1].text += " ";
              }
            } else if (screen && screen.lines.length > 0) {
              screen.lines[screen.lines.length - 1].color = sccFunc.colorMapping[color];
              if (sccCode === "{COLOR:WHITE}") {
                screen.lines[screen.lines.length - 1].text = closeOpenStyles(screen.lines[screen.lines.length - 1].text);
              }
              underline ? screen.lines[screen.lines.length - 1].text += "<u>" : null;
              screen.lines[screen.lines.length - 1].text += `<${color}>`;
              if (sccCode === "{COLOR:WHITE}" && codes[index + 1] && !nextCodeStartsWithSpace(codes[index + 1])) {
                screen.lines[screen.lines.length - 1].text += " ";
              }
            }
          } else if (sccCode.includes("{BACKGROUND:")) {
            let bgColor = sccCode.split(":")[1].split(";")[0].split("}")[0];
            let opacity = 1;
            if (sccCode.includes("TRANSPARENT") && bgColor != "NONE") {
              opacity = 0.5;
            } else if (bgColor === "NONE") {
              opacity = 0.0;
            }
            if (displayStyle === "popOn") {
              buffer.lines[buffer.lines.length - 1].background = sccFunc.colorMapping[bgColor];
              buffer.lines[buffer.lines.length - 1].opacity = opacity;
            } else if (screen && screen.lines.length > 0) {
              screen.lines[screen.lines.length - 1].background = sccFunc.colorMapping[bgColor];
              screen.lines[screen.lines.length - 1].opacity = opacity;
            }
          } else if (sccCode === "{NEW LINE}") {
            if (displayStyle === "popOn") {
              if (buffer.lines.length > 0 && buffer.lines[buffer.lines.length - 1].text) {
                buffer.insertLine();
              }
            } else if (displayStyle === 'rollUp2' || displayStyle === 'rollUp3' || displayStyle === 'rollUp4') {
              if (screen && screen.lines[screen.lines.length - 1].text) {
                /* Add outcode for the top line of event */
                if (displayStyle === 'rollUp2') {
                  if (displays.length > 0 && !displays[displays.length - 1].end) {
                    displays[displays.length - 1].end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
                  }
                  if (screen) {
                    if (!screen.end) {
                      screen.end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
                    }
                    displays.push(screen);
                  }
                  screen = new sccFunc.Display({
                    style: "Roll-Up 2",
                    start: tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame)
                  });
                  screen.insertLine({
                    yPos: 13
                  });
                } else if (displayStyle === 'rollUp3') {
                  if (displays.length > 2 && !displays[displays.length - 1].end) {
                    displays[displays.length - 2].end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
                  }
                  if (screen) {
                    if (!screen.end) {
                      screen.end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
                    }
                    displays.push(screen);
                  }
                  screen = new sccFunc.Display({
                    style: "Roll-Up 3",
                    start: tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame)
                  });
                  screen.insertLine({
                    yPos: 12
                  });
                } else if (displayStyle === 'rollUp4') {
                  if (displays.length > 0 && !displays[displays.length - 1].end) {
                    displays[displays.length - 3].end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
                  }
                  if (screen) {
                    if (!screen.end) {
                      screen.end = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
                    }
                    displays.push(screen);
                  }
                  screen = new sccFunc.Display({
                    style: "Roll-Up 4",
                    start: tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame)
                  });
                  screen.insertLine({
                    yPos: 11
                  });
                }
              }
            } else if (displayStyle === 'paintOn') {
              if (screen) {
                screen.insertLine({
                  style: "Paint-On"
                });
              }
            }
          } else if (sccCode === "{BACKSPACE}") {
            if (displayStyle === "popOn") {
              if (buffer.lines && buffer.lines[buffer.lines.length - 1].text && !buffer.lines[buffer.lines.length - 1].text.endsWith('>')) {
                buffer.lines[buffer.lines.length - 1].text = buffer.lines[buffer.lines.length - 1].text.slice(0, -1);
              }
            } else if (screen && screen.lines.length > 0 && screen.lines[screen.lines.length - 1].text && !screen.lines[screen.lines.length - 1].text.endsWith('>')) {
              screen.lines[screen.lines.length - 1].text = screen.lines[screen.lines.length - 1].text.slice(0, -1);
            }
          } else if (sccCode === "{DELETE ROW}") {
            if (displayStyle === "popOn") {
              buffer.lines[buffer.lines.length - 1].text = "";
              buffer.lines[buffer.lines.length - 1].yPos = 0;
            } else if (screen) {
              screen.lines[screen.lines.length - 1].text = "";
              screen.lines[screen.lines.length - 1].yPos = 0;
            }
          } else if (sccCode !== "{FILLER}") {
            /* An extended character (0x12xx/0x13xx) overwrites the standard fallback character sent before it. Its redundant repeat overwrites the first copy. */
            let isExtendedChar = sccLookup.specialCharsFilter.indexOf(sccCode) > -1;
            if (displayStyle === "popOn") {
              if (isExtendedChar && buffer.lines[buffer.lines.length - 1].text && !buffer.lines[buffer.lines.length - 1].text.endsWith('>')) {
                buffer.lines[buffer.lines.length - 1].text = buffer.lines[buffer.lines.length - 1].text.slice(0, -1);
              }
              buffer.lines[buffer.lines.length - 1].text += sccCode;
            } else if (screen) {
              if (isExtendedChar && screen.lines[screen.lines.length - 1].text && !screen.lines[screen.lines.length - 1].text.endsWith('>')) {
                screen.lines[screen.lines.length - 1].text = screen.lines[screen.lines.length - 1].text.slice(0, -1);
              }
              screen.lines[screen.lines.length - 1].text += sccCode;
              if (screen.start === false) {
                screen.start = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
              }
            }
          }
        } else {
          [code.substring(0, 2), code.substring(2, 4)].filter(sccCode => {
            return sccLookup.all[sccCode] !== undefined && sccLookup.all[sccCode] !== "{FILLER}";
          }).forEach(sccCode => {
            // console.log(sccCode, sccLookup.all[sccCode]);
            if (displayStyle === "popOn") {
              if (buffer.lines.length === 0) {
                buffer.insertLine();
              }
              buffer.lines[buffer.lines.length - 1].text += sccLookup.all[sccCode];
            } else if (screen.lines) {
              screen.lines[screen.lines.length - 1].text += sccLookup.all[sccCode];
              if (screen.start === false) {
                screen.start = tcLib.tcToSec(clock.toString(), options.frameRate, options.dropFrame);
              }
            }
          });
        }
      });
    });

    // console.log(JSON.stringify(displays, null, 4));
    displays.forEach(display => {
      events.push(sccFunc.decodeDisplay(display, options.window));
    });

    // console.log(JSON.stringify(events, null, 4));
    return events;
  },
  encode: function (eventGroup, options) {
    let encodingOptions = getFormatOptions(options.formatOptions),
      channel = "cc1";
    if (encodingOptions["Channel"]) {
      channel = encodingOptions["Channel"].toLowerCase();
    }
    channel = channel.replace("ch0", "cc");
    let eventGroupChannelMappings = {};
    eventGroupChannelMappings[channel] = 0;
    return sceneristAdvanced.encode([eventGroup], {
      ...options,
      formatOptions: [...options.formatOptions, {
        "name": "Event Group Channel Mappings",
        "selected": eventGroupChannelMappings
      }]
    });
  },
  preProcess: {
    encode: function (eventGroup, options) {
      let selectedEvents = [];
      eventGroup.events.forEach((event, index) => {
        if (autoFormatCheck(event, 4, 32)) {
          selectedEvents.push(index);
        }
      });
      if (selectedEvents.length > 0) {
        for (let i = 0; i < 2; i++) {
          eventGroup = autoFormat(eventGroup, 4, 32, 0.83, false, selectedEvents);
        }
      }
      eventGroup.events.forEach((event, index, events) => {
        // console.log("---------");
        // console.log("BEFORE:");
        // console.log(event.text);

        sccLookup.specialCharsFilter.forEach(specialChar => {
          let regexPattern = new RegExp(`\\` + specialChar, "g");
          events[index].text = event.text.replace(regexPattern, sccLookup.specialCharacterReplacement[specialChar] + specialChar);
        });
        events[index].text = sccFunc.duplicateMusicNotes(events[index].text);

        // console.log("AFTER:");
        // console.log(events[index].text);
      });
      return removeInvalidEvents(eventGroup);
    },
    decode: function (input) {
      return eol.lf(input.toLowerCase().replace(/ +/g, " ").trim());
    }
  },
  postProcess: {
    encode: function (output) {
      return output;
    },
    decode: function (eventGroup, options) {
      /* 
          We decoded the SCC using the 32x15 grid but now we need to center that grid over the window. There is a fixed offset of 30% (x) and 10% (y). This is due to the caption area being 70% of the frames width and 90% of the frames height. 
      */
      /* if (options.jobInfo.target_profile !== "scenerist" && options.jobInfo.target_profile !== "closedCaptionProject") {
          eventGroup = convertToPopOn(eventGroup, 2, 32);
      } */

      if (eventGroup.events.length > 0 && eventGroup.events[0].style === "Pop-On" && stripTags(eventGroup.events[0].text).trim() == "") {
        eventGroup.events.shift();
      }
      eventGroup.events.forEach((event, index, events) => {
        events[index].text = sccFunc.replaceMusicNotes(events[index].text);
      });

      // console.log(JSON.stringify(eventGroup, null, 4));
      return eventGroup;
    }
  }
};